/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import * as AwsAuth from 'aws-amplify/auth';
import {
  signIn as awsSignIn,
  signOut as awsSignOut,
  getCurrentUser,
  confirmUserAttribute,
  sendUserAttributeVerificationCode,
  resetPassword,
  confirmResetPassword,
  AuthUser,
} from 'aws-amplify/auth';
import { IUserCredentials } from '../interfaces/auth.interface';
import {
  CognitoIdentityProviderClient,
  RespondToAuthChallengeCommand,
} from "@aws-sdk/client-cognito-identity-provider";

const cognitoUser$ = new BehaviorSubject<AuthUser | null>(null);

const {
  REGION,
  USER_POOL_WEB_CLIENT_ID,
} = JSON.parse(
  process.env.REACT_APP_AMPLIFY_CONFIG || ''
);

const client = new CognitoIdentityProviderClient({ region: REGION });

const setCognitoUser = (state: AuthUser) => {
  cognitoUser$.next(state);
};

const getCognitoUser = async (): Promise<AuthUser | null> => {
  try {
    const user = await getCurrentUser();
    return user;
  } catch (e) {
    console.warn('No authenticated user found');
    return null;
  }
};

const useCognitoUserState = (): [AuthUser | null, (user: AuthUser) => void] => {
  const [value, setState] = useState<AuthUser | null>(cognitoUser$.getValue());
  useEffect(() => {
    const sub = cognitoUser$.subscribe((s) => setState(s));
    return () => sub.unsubscribe();
  }, []);
  return [value, setCognitoUser];
};

const signIn = ({ username, password }: IUserCredentials) =>
  awsSignIn({ username, password });

const signOut = async () => {
  await awsSignOut();
  localStorage.removeItem('user');
};

const forgotPassword = (username: string) =>
  resetPassword({ username: username.trim().toLowerCase() });

const forgotPasswordSubmit = (
  username: string,
  code: string,
  password: string
) =>
  confirmResetPassword({
    username: username.toLowerCase(),
    confirmationCode: code,
    newPassword: password,
  });

const verifyEmail = (code: string) =>
  confirmUserAttribute({
    confirmationCode: code,
    userAttributeKey: 'email',
  });

const resendCode = () =>
  sendUserAttributeVerificationCode({ userAttributeKey: 'email' });

const currentAuthenticatedUser = () => getCurrentUser();

export async function completeNewPassword({
  username,
  newPassword,
}: {
  username: string;
  newPassword: string;
}) {
  const command = new RespondToAuthChallengeCommand({
    ClientId: USER_POOL_WEB_CLIENT_ID,
    ChallengeName: "NEW_PASSWORD_REQUIRED",
    ChallengeResponses: {
      USERNAME: username,
      NEW_PASSWORD: newPassword,
    },
  });

  const response = await client.send(command);
  return response;
}

export default {
  signIn,
  signOut,
  currentAuthenticatedUser,
  completeNewPassword,
  setCognitoUser,
  getCognitoUser,
  useCognitoUserState,
  forgotPassword,
  forgotPasswordSubmit,
  verifyEmail,
  resendCode,
};